import { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import WaitlistModal from '@/components/WaitlistModal';
import HeroSection from '@/components/sections/HeroSection';
import ProblemSection from '@/components/sections/ProblemSection';
import HowItWorksSection from '@/components/sections/HowItWorksSection';
import TrustScoreSection from '@/components/sections/TrustScoreSection';
import ProofSection from '@/components/sections/ProofSection';
import IntrowspectSection from '@/components/sections/IntrowspectSection';
import MembersSection from '@/components/sections/MembersSection';
import SpecterSection from '@/components/sections/SpecterSection';
import NoticesSection from '@/components/sections/NoticesSection';
import InstitutionalPartnersSection from '@/components/sections/InstitutionalPartnersSection';
import FAQSection from '@/components/sections/FAQSection';
import CTASection from '@/components/sections/CTASection';

const LandingPage = () => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const pageRef = useRef(null);
  const location = useLocation();

  const { scrollYProgress } = useScroll({ target: pageRef });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);

  // Scroll to hash section when navigated from another page
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  const openWaitlist = () => setIsWaitlistOpen(true);
  const closeWaitlist = () => setIsWaitlistOpen(false);

  return (
    <div ref={pageRef} className="min-h-screen bg-[#EDECEA] relative overflow-hidden" data-testid="landing-page">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Seamless background with parallax */}
      <motion.div
        className="pointer-events-none absolute inset-0 w-full overflow-hidden z-0"
        style={{ y: bgY, height: '115%' }}
      >
        <svg className="absolute top-0 left-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 6000" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M-200 0C100 200 400 100 720 300C1040 500 1200 200 1600 400C1600 400 1700 600 1400 800C1100 1000 200 900 -100 1100C-400 1300 300 1500 720 1400C1140 1300 1600 1500 1500 1800C1400 2100 400 2000 100 2200C-200 2400 600 2600 1000 2500C1400 2400 1700 2700 1400 3000C1100 3300 -100 3100 -200 3400C-300 3700 800 3800 1200 3700C1600 3600 1800 4000 1300 4200C800 4400 -200 4300 -100 4600C0 4900 900 5000 1440 4900L1440 6000L-200 6000Z" fill="url(#bgGrad)" />
          <circle cx="350" cy="700" r="400" fill="url(#glow1)" />
          <circle cx="1100" cy="2000" r="450" fill="url(#glow2)" />
          <circle cx="300" cy="3500" r="400" fill="url(#glow1)" />
          <circle cx="1050" cy="5000" r="350" fill="url(#glow2)" />
          <defs>
            <linearGradient id="bgGrad" x1="720" y1="0" x2="720" y2="6000" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#0F1B2D" stopOpacity="0.04" />
              <stop offset="0.2" stopColor="#C4A35A" stopOpacity="0.03" />
              <stop offset="0.4" stopColor="#0F1B2D" stopOpacity="0.05" />
              <stop offset="0.6" stopColor="#C4A35A" stopOpacity="0.025" />
              <stop offset="0.8" stopColor="#0F1B2D" stopOpacity="0.04" />
              <stop offset="1" stopColor="#C4A35A" stopOpacity="0.03" />
            </linearGradient>
            <radialGradient id="glow1" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#C4A35A" stopOpacity="0.08" />
              <stop offset="1" stopColor="#C4A35A" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="glow2" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#0F1B2D" stopOpacity="0.06" />
              <stop offset="1" stopColor="#0F1B2D" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </motion.div>

      <div className="relative z-10">
        <Navigation onWaitlistClick={openWaitlist} />
        
        <main>
          <HeroSection onWaitlistClick={openWaitlist} />
          <ProblemSection />
          <HowItWorksSection />
          <TrustScoreSection />
          <MembersSection />
          <ProofSection onWaitlistClick={openWaitlist} />
          <InstitutionalPartnersSection />
          <IntrowspectSection />
          <SpecterSection />
          <NoticesSection />
          <FAQSection />
          <CTASection onWaitlistClick={openWaitlist} />
        </main>

        <Footer />
      </div>

      <WaitlistModal isOpen={isWaitlistOpen} onClose={closeWaitlist} />
    </div>
  );
};

export default LandingPage;
